import {type AutocompleteState, createAutocomplete} from '@algolia/autocomplete-core'
import {announce} from '@react-aria/live-announcer'

import CloseSvg from '@/assets/icons/close.svg?react'
import MicrophoneSvg from '@/assets/icons/microphone.svg?react'
import MuteSvg from '@/assets/icons/mute.svg?react'
import searchSvg from '@/assets/icons/search.svg'
import useVoiceToText from '@/hooks/useVoiceToText'
import {useLocale} from '@/lib/locale/locale'
import {HomeRoute} from '@/routeRegistry'
import {getSearchSuggestions} from '@/services/searchService'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import debouncePromise from '@/utils/debouncePromise'
import markAsMemoized from '@/utils/react/markAsMemoized'

import style from './SearchInputWithAutocomplete.module.scss'
// @ts-expect-error -- workaround
const debounced: <T extends unknown[]>(items: T) => Promise<T> = debouncePromise(
  async items => Promise.resolve(items),
  300,
)

export interface SearchInputControl {
  setSearchInput: MemoizedCallbackOrDispatch<string>
  submit: () => void
  focus: () => void
}

interface Props {
  ref?: Ref<SearchInputControl | null>
  placeholderText?: string
  variant?: 'navbar' | 'main'
}

interface BaseItem extends Record<string, unknown> {
  content: string
}

const ProductItem = memo(function ProductItem({
  item,
  isActive,
  query,
  ...props
}: {
  item: BaseItem
  query: string
  isActive: boolean
} & ComponentProps<'li'>) {
  const q = query.toLowerCase()

  // TODO: improve hightlighting: highlight all occurences of the query, case-insensitive
  const highlighted = item.content.startsWith(q) ? item.content.slice(q.length) : false

  return (
    <li className={clsx(style.Item, isActive && style.IsActive)} {...props}>
      <Link
        to={HomeRoute.fullPath}
        search={{
          q: item.content,
        }}
        className={style.ItemLink}
      >
        <div className={style.ItemContent}>
          <img className={style.ItemDecor} src={searchSvg} alt='' />
          <div className={style.ItemLabel}>
            {highlighted ? (
              <>
                {q}
                <mark>{highlighted}</mark>
              </>
            ) : (
              item.content
            )}
          </div>
        </div>
      </Link>
    </li>
  )
})

// function preloadResult(query: string, queryClient: QueryClient) {
//   void queryClient.ensureQueryData(
//     queries.getQueryOptions({
//       params: [query],
//       name: 'searchResult',
//       queryFn: ({signal, queryKey}) => getSearchResult({q: queryKey[1], signal, preload: true}),
//     }),
//   )
// }

// function cancelAllPreloadResults(queryClient: QueryClient) {
//   // TODO: support calling from queries utilities
//   return queryClient.cancelQueries({
//     queryKey: ['searchResult'],
//   })
// }

// Follow this guide: https://www.algolia.com/doc/ui-libraries/autocomplete/guides/creating-a-renderer/
export default memo(function SearchInputWithAutocomplete({ref, placeholderText, variant}: Props) {
  const router = useRouter()
  const navigate = useNavigate()
  const [locale] = useLocale()
  // const scheduler = useIdleTimeScheduler()

  const inputRef = useRef<HTMLInputElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const [autocompleteState, setAutocompleteState] = useState<AutocompleteState<BaseItem>>({
    collections: [],
    completion: null,
    context: {},
    isOpen: false,
    query: '',
    activeItemId: null,
    status: 'idle',
  })

  const getHref = useCallback(
    (q: string) => {
      return router.buildLocation({
        to: HomeRoute.fullPath,
        search: {
          q,
        },
      }).href
    },
    [router],
  )

  const autocomplete = useMemo(
    () =>
      createAutocomplete<BaseItem, SyntheticEvent, React.MouseEvent, React.KeyboardEvent>({
        // debug: true, // Keep it open when development
        placeholder: placeholderText ?? 'Search for a product or item',
        initialState: {
          // query: '',
        },
        openOnFocus: true,
        onStateChange(params) {
          setAutocompleteState(params.state)
        },
        navigator: {
          navigate({itemUrl}) {
            void navigate({
              to: itemUrl,
              resetScroll: true,
            })
          },
          navigateNewTab({itemUrl}) {
            const windowReference = window.open(itemUrl, '_blank', 'noopener')

            if (windowReference) {
              windowReference.focus()
            }
          },
          navigateNewWindow({itemUrl}) {
            window.open(itemUrl, '_blank', 'noopener')
          },
        },
        async getSources() {
          return debounced([
            {
              sourceId: 'suggestions',
              getItemInputValue(params) {
                return params.item.content
              },
              async getItems(params) {
                if (params.query === '' || params.query === ' ') return []
                try {
                  const result = await getSearchSuggestions({locale, query: params.query})
                  return result.map(item => ({
                    content: item,
                  }))
                } catch {
                  return []
                }
              },
              getItemUrl(params) {
                return getHref(params.item.content)
              },
              onSelect(params) {
                params.setIsOpen(false)
                inputRef.current?.blur()

                if (params.itemUrl)
                  params.navigator.navigate({
                    itemUrl: params.itemUrl,
                    item: params.item,
                    state: params.state,
                  })
              },
              onResolve: params => {
                if (!Array.isArray(params.items)) {
                  return
                }

                const totalSuggestions = params.items.reduce(
                  (total: number, item) => total + (Array.isArray(item) ? item.length : 1),
                  0,
                )

                announce(
                  `Found ${totalSuggestions} suggestions for "${params.state.query}"`,
                  'polite',
                )

                // await cancelAllPreloadResults(queryClient)

                params.items.forEach(() => {
                  // NOTE: we disable preloading to saving computational resources
                  // if (!Array.isArray(item)) return
                  // item.slice(0, 3).forEach(data => {
                  //   if (!(typeof data == 'object' && data && 'content' in data)) return
                  //   const content = data.content
                  //   if (typeof content !== 'string') return
                  //   scheduler.schedule(() => {
                  //     preloadResult(content, queryClient)
                  //   })
                  // })
                })
              },
              onActive() {
                // NOTE: we disable preloading to saving computational resources
                // scheduler.schedule(() => {
                //   preloadResult(params.item.content, queryClient)
                // })
              },
            },
          ])
        },
        onSubmit(params) {
          void navigate({
            to: HomeRoute.fullPath,
            search: {
              q: params.state.query,
            },
            resetScroll: true,
          })
        },
      }),
    [navigate, getHref, locale, placeholderText],
  )
  const latestAutocomplete = useLatest(autocomplete)

  const {getEnvironmentProps} = autocomplete

  useEffect(() => {
    if (!formRef.current || !panelRef.current || !inputRef.current) {
      return undefined
    }

    // TODO: check this error
    // eslint-disable-next-line @typescript-eslint/unbound-method -- dont know how to fix it
    const {onTouchStart, onTouchMove, onMouseDown} = getEnvironmentProps({
      formElement: formRef.current,
      inputElement: inputRef.current,
      panelElement: panelRef.current,
    })

    const abortController = new AbortController()

    window.addEventListener('mousedown', onMouseDown, {signal: abortController.signal})
    window.addEventListener('touchstart', onTouchStart, {signal: abortController.signal})
    window.addEventListener('touchmove', onTouchMove, {signal: abortController.signal})

    return () => {
      abortController.abort()
    }
  }, [getEnvironmentProps, autocompleteState.isOpen])

  const {startListening, stopListening, transcript, reset, isSpeechRecognitionAvailable} =
    useVoiceToText()
  const latestSpeechRecognitionIsAvailable = useLatest(isSpeechRecognitionAvailable)
  const [isListening, setIsListening] = useState(false)
  const latestStartListening = useLatest(startListening)
  const latestIsListening = useLatest(isListening)
  const latestStopListening = useLatest(stopListening)
  const latestReset = useLatest(reset)

  const onVoiceModePressed = useCallback<MouseEventHandler<HTMLButtonElement>>(e => {
    e.preventDefault()
    if (!latestSpeechRecognitionIsAvailable.current) return
    if (latestIsListening.current) {
      latestOnVoiceModeEnd.current()
    } else {
      latestStartListening.current()
      setIsListening(true)
      announce('Listening...', 'polite')
    }
  }, [])

  const onVoiceModeEnd = useCallback(() => {
    if (!latestIsListening.current) return
    if (!latestSpeechRecognitionIsAvailable.current) return
    latestStopListening.current()
    latestReset.current()
    setIsListening(false)
    inputRef.current?.focus()
  }, [])
  const latestOnVoiceModeEnd = useLatest(onVoiceModeEnd)

  useEffect(() => {
    if (!transcript) return
    latestAutocomplete.current.setQuery(transcript)
    void latestAutocomplete.current.refresh()
  }, [transcript])

  useImperativeHandle(ref, () => {
    return {
      setSearchInput: markAsMemoized(latestAutocomplete.current.setQuery),
      submit: () => {
        formRef.current?.requestSubmit()
      },
      focus: () => {
        inputRef.current?.focus()
      },
    }
  }, [])

  return (
    <div
      className={clsx(
        style.AutoCompleteContainer,
        variant === 'main' && style.AutoCompleteContainerMain,
      )}
      {...autocomplete.getRootProps({role: 'none'})}
    >
      <form
        ref={formRef}
        className={style.Form}
        {...autocomplete.getFormProps({inputElement: inputRef.current})}
      >
        <div className={style.InputWrapper}>
          <input
            className={style.Input}
            ref={inputRef}
            {...autocomplete.getInputProps({inputElement: inputRef.current, role: 'combobox'})}
          />
        </div>
        <div className={style.InputWrapperPrefix}></div>
        <div className={style.InputWrapperSuffix}>
          {isSpeechRecognitionAvailable && (
            <button
              type='button'
              onClick={onVoiceModePressed}
              className={clsx(style.ClearButton)}
              aria-label={isListening ? 'Stop dictation mode' : 'Start dictation mode'}
              onBlur={onVoiceModeEnd}
            >
              {isListening ? <MuteSvg /> : <MicrophoneSvg />}
            </button>
          )}
          <button
            className={clsx(
              style.ClearButton,
              variant === 'main' && style.ClearButton,
              autocompleteState.query.length === 0 && style.IsHidden,
            )}
            aria-label='Clear search'
            type='reset'
            aria-disabled={autocompleteState.query.length === 0}
            tabIndex={autocompleteState.query.length === 0 ? -1 : 0}
            onClick={() => {
              reset()
            }}
          >
            <CloseSvg aria-hidden='true' />
          </button>
          <label className={style.Label} {...autocomplete.getLabelProps({})}>
            <button className={style.SubmitButton} type='submit' aria-label='Search'>
              <img
                src={searchSvg}
                alt=''
                className={clsx(style.SubmitIcon, variant === 'main' && style.SubmitIconMain)}
              />
            </button>
          </label>
        </div>
      </form>

      {autocompleteState.isOpen && (
        <div
          ref={panelRef}
          className={clsx(style.Panel, variant === 'main' && style.Main)}
          {...autocomplete.getPanelProps({})}
        >
          <div className={style.PanelLayout}>
            {autocompleteState.collections.map((collection, index) => {
              const {source, items} = collection

              return (
                <section key={`source-${index}`} className={style.Source}>
                  {autocompleteState.query && (
                    <VisuallyHidden>{`Found ${items.length} suggestions for "${autocompleteState.query}"`}</VisuallyHidden>
                  )}
                  {items.length > 0 && (
                    <ul className={style.List} {...autocomplete.getListProps()}>
                      {items.map((item, index) => {
                        const rest = autocomplete.getItemProps({
                          item,
                          source,
                        })
                        return (
                          <ProductItem
                            key={index}
                            isActive={autocompleteState.activeItemId === index}
                            query={autocompleteState.query}
                            item={item}
                            {...rest}
                          />
                        )
                      })}
                    </ul>
                  )}
                </section>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
})
